import React, { memo, useEffect, useMemo, useRef, useState } from 'react'
import Drawer from 'components/shared/Drawer';
import strings from 'strings';
import { REPORT_TYPE } from 'constants/constantVariables';
import Loader from 'components/shared/Loader';
import dayjs from 'dayjs';
import MonitoringSetup from 'components/Monitoring/MonitoringSetup';
import MonitoringNotifyMe from 'components/Monitoring/MonitoringNotifyMe';
import AuthService from 'tools/AuthService';
import './ReportsMonitoring.scss';

const monitoringStrings = strings.MONITORINGS;

// Per-report display label. Owner-scoped reports (UCC, Background) name their entity.
const reportLabel = (report) => {
    if (report.reportType === REPORT_TYPE.UCC) return `UCC search (${report.entityName})`;
    if (report.reportType === REPORT_TYPE.LEXIS_VERIFICATION) return monitoringStrings.VERIFICATION;
    if (report.reportType === REPORT_TYPE.LEXIS_BACKGROUND) return `${monitoringStrings.BACKGROUND_CHECK} (${report.entityName})`;
    if (report.reportType === REPORT_TYPE.FMCSA) return 'FMCSA';
    if (report.reportType === REPORT_TYPE.SOS) return 'Secretary of State';
    return report.reportType;
};

const KNOWN_CADENCES = ['HOURLY', 'EVERY_6_HOURS', 'DAILY', 'WEEKLY', 'EVERY_2_WEEKS', 'EVERY_1_MONTH',
    'EVERY_2_MONTHS', 'EVERY_3_MONTHS', 'EVERY_4_MONTHS', 'EVERY_6_MONTHS', 'EVERY_12_MONTHS'];

// The config DTO's reports carry no id, so identify each report by reportType + entity (unique per row).
const reportKey = (r) => `${r.reportType}::${r.entityId || ''}`;

// Thin wrapper around the shared MonitoringSetup component (single-business mode). Loads the config, adapts
// the report list to the shared shape, and maps the shared payload back to the per-app update DTO.
export const ReportsMonitoringDrawer = memo((props) => {
    const {
        open,
        onClose,
        getMonitoringConfig,
        updateMonitoringConfig,
        roxAppId,
        getApplication,
    } = props;

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState(false);
    // The config for THIS drawer's applicant, taken from the request's own response rather than the shared
    // Redux monitoringConfig slot (keyed only by app, so a slower in-flight request for a different applicant
    // could overwrite it). reqIdRef ignores stale responses.
    const [localConfig, setLocalConfig] = useState(null);
    const reqIdRef = useRef(0);

    useEffect(() => {
        const myReqId = ++reqIdRef.current;
        const fetchConfig = async () => {
            setLoading(true);
            setLoadError(false);
            try {
                const res = await getMonitoringConfig(roxAppId);
                if (reqIdRef.current !== myReqId) return; // superseded by a newer applicant's request
                setLocalConfig(res && res.response ? res.response : null);
            } catch (e) {
                // Flag an error and block the form rather than render/save a stale config under this
                // roxAppId. The request helper surfaces the error toast.
                if (reqIdRef.current === myReqId) setLoadError(true);
            } finally {
                if (reqIdRef.current === myReqId) setLoading(false);
            }
        };
        if (open && roxAppId) {
            setLocalConfig(null);
            fetchConfig();
        }
    }, [open, roxAppId, getMonitoringConfig]);

    const rawReports = localConfig?.reports || [];

    // Adapt to the shared component's report shape. Memoized on the loaded config so the setup component's
    // internal state isn't reset on every parent re-render.
    const reports = useMemo(() => rawReports.map(r => ({
        key: reportKey(r),
        reportType: r.reportType,
        label: reportLabel(r),
        credits: r.creditsCost || 0,
        available: !!r.available,
        reason: r.message,
        entityId: r.entityId,
    })), [rawReports]);

    const initialSelections = useMemo(() => {
        const sel = {};
        rawReports.forEach(r => {
            if (!r.enabled) return;
            const seed = r.frequency || localConfig?.frequency;
            sel[reportKey(r)] = KNOWN_CADENCES.includes(seed) ? seed : 'EVERY_1_MONTH';
        });
        return sel;
    }, [rawReports, localConfig]);

    const initialStart = localConfig?.startDate ? dayjs(localConfig.startDate).format('YYYY-MM-DD') : null;
    const initialEnd = localConfig?.endDate ? dayjs(localConfig.endDate).format('YYYY-MM-DD') : null;

    const handlePrimary = ({ selectedReports, startDate, endDate }) => {
        const chosen = new Map(selectedReports.map(s => [s.report.key, s.cadence]));
        const entityConfigurations = reports.map(r => ({
            reportType: r.reportType,
            entityId: r.entityId,
            enabled: chosen.has(r.key),
            frequency: chosen.get(r.key) || null,
        }));
        const payload = {
            enabled: entityConfigurations.some(e => e.enabled),
            explicitWindow: true,
            startDate,
            endDate,
            entityConfigurations,
        };
        setSaving(true);
        updateMonitoringConfig(roxAppId, payload)
            .then(() => { onClose(); getApplication(); })
            .finally(() => setSaving(false));
    };

    return (
        <Drawer open={open} onClose={onClose} title={monitoringStrings.TITLE}>
            <div className='posting-container'>
                {loading ? (
                    <Loader />
                ) : loadError ? (
                    <div className='monSetup__loadError'>
                        Couldn’t load monitoring configuration. Please close and reopen to try again.
                    </div>
                ) : (
                    <>
                    {/* Advanced monitoring only (the backend sends notifyMe as null otherwise); not for read-only users. */}
                    {localConfig?.notifyMe != null && !AuthService.getClientFeatures().isReadOnlyUser && (
                        <MonitoringNotifyMe
                            applicationId={roxAppId}
                            notifyMe={localConfig.notifyMe}
                            requesterName={localConfig.requesterName}
                            alertsPaused={!!localConfig.alertsPaused}
                        />
                    )}
                    <MonitoringSetup
                        mode='single'
                        reports={reports}
                        balance={typeof localConfig?.balance === 'number' ? localConfig.balance : null}
                        initialSelections={initialSelections}
                        initialStart={initialStart}
                        initialEnd={initialEnd}
                        initialOngoing={!initialEnd}
                        busy={saving}
                        onPrimary={handlePrimary}
                        onCancel={onClose}
                    />
                    </>
                )}
            </div>
        </Drawer>
    );
});
